/* ==========================================================================
   MOMTAZ — theme.js
   Built by AY Develops
   No dependencies. Progressive enhancement only.
   ========================================================================== */

(function () {
  'use strict';

  var doc = document;
  var root = doc.documentElement;
  var cfg = window.theme || {};
  var settings = cfg.settings || {};
  var routes = cfg.routes || {};
  var strings = cfg.strings || {};

  var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------------------------------------------------------------- utils */

  function $(sel, ctx) {
    return (ctx || doc).querySelector(sel);
  }

  function $$(sel, ctx) {
    return Array.prototype.slice.call((ctx || doc).querySelectorAll(sel));
  }

  function on(el, type, fn, opts) {
    if (el) el.addEventListener(type, fn, opts);
  }

  function debounce(fn, wait) {
    var t;
    return function () {
      var args = arguments;
      var self = this;
      clearTimeout(t);
      t = setTimeout(function () {
        fn.apply(self, args);
      }, wait);
    };
  }

  function rafThrottle(fn) {
    var queued = false;
    return function () {
      if (queued) return;
      queued = true;
      window.requestAnimationFrame(function () {
        queued = false;
        fn();
      });
    };
  }

  function announce(message) {
    var region = $('.a11y-announce');
    if (!region) return;
    region.textContent = '';
    window.setTimeout(function () {
      region.textContent = message;
    }, 60);
  }

  function formatMoney(cents) {
    var format = settings.moneyFormat || '${{amount}}';
    var value = (cents / 100).toFixed(2);
    var parts = value.split('.');
    var whole = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');

    return format
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, whole)
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, whole + ',' + parts[1])
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, whole)
      .replace(/\{\{\s*amount\s*\}\}/g, whole + '.' + parts[1]);
  }

  /* ------------------------------------------------------- scroll locking */

  var lockCount = 0;
  var lockOffset = 0;

  // Pinning body in the theme editor fights the editor's own scrolling and
  // makes previewed overlays look broken, so skip the lock in design mode.
  var designMode = !!(window.Shopify && window.Shopify.designMode);

  function lockScroll() {
    if (designMode) return;
    if (lockCount === 0) {
      lockOffset = window.scrollY;
      doc.body.style.top = -lockOffset + 'px';
      doc.body.style.position = 'fixed';
      doc.body.style.width = '100%';
      doc.body.classList.add('is-locked');
    }
    lockCount += 1;
  }

  function unlockScroll() {
    if (designMode) return;
    lockCount = Math.max(0, lockCount - 1);
    if (lockCount === 0) {
      doc.body.style.position = '';
      doc.body.style.top = '';
      doc.body.style.width = '';
      doc.body.classList.remove('is-locked');
      // Must be instant: html has scroll-behavior:smooth, so a plain scrollTo
      // would animate the page back and look like the menu threw you around.
      try {
        window.scrollTo({ top: lockOffset, left: 0, behavior: 'instant' });
      } catch (e) {
        window.scrollTo(0, lockOffset);
      }
    }
  }

  /* ----------------------------------------------------------- focus trap */

  var FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), summary, [tabindex]:not([tabindex="-1"])';

  // `trap` false is for disclosure panels whose toggle sits outside the panel
  // (the mobile menu) — trapping there would make the close button unreachable.
  function trapFocus(container, initial, trap, moveFocus) {
    var previous = doc.activeElement;

    function keydown(event) {
      if (event.key !== 'Tab') return;
      var items = $$(FOCUSABLE, container).filter(function (el) {
        return el.offsetParent !== null || el === doc.activeElement;
      });
      if (!items.length) return;
      var first = items[0];
      var last = items[items.length - 1];

      if (event.shiftKey && doc.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && doc.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    if (trap !== false) container.addEventListener('keydown', keydown);

    if (moveFocus !== false) {
      window.setTimeout(function () {
        (initial || $(FOCUSABLE, container) || container).focus();
      }, 80);
    }

    return function release() {
      container.removeEventListener('keydown', keydown);
      if (moveFocus !== false && previous && typeof previous.focus === 'function') {
        previous.focus();
      }
    };
  }

  /* ------------------------------------------------------------- overlays */

  var openLayers = [];

  var Layer = {
    open: function (el, opts) {
      if (!el || el.classList.contains('is-open')) return;
      opts = opts || {};
      el.classList.add('is-open');
      el.removeAttribute('aria-hidden');

      // The hide-on-scroll class applies a transform to the header shell, which
      // would become the containing block for anything fixed inside it.
      var shell = $('.shell__header');
      if (shell) shell.classList.remove('is-hidden');

      lockScroll();
      var release = trapFocus(
        el,
        opts.initialFocus ? $(opts.initialFocus, el) : null,
        opts.trap,
        opts.focus
      );
      openLayers.push({ el: el, release: release, onClose: opts.onClose });
      doc.dispatchEvent(new CustomEvent('layer:open', { detail: { el: el } }));
    },

    close: function (el) {
      var index = -1;
      for (var i = openLayers.length - 1; i >= 0; i--) {
        if (!el || openLayers[i].el === el) {
          index = i;
          break;
        }
      }
      if (index === -1) return;
      var layer = openLayers[index];
      layer.el.classList.remove('is-open');
      layer.el.setAttribute('aria-hidden', 'true');
      layer.release();
      // Drop the layer and restore the scroll position *before* onClose, so a
      // callback that re-reads scroll state (the header) sees the real values.
      openLayers.splice(index, 1);
      unlockScroll();
      if (typeof layer.onClose === 'function') layer.onClose();
    },

    closeAll: function () {
      while (openLayers.length) Layer.close();
    },

    isOpen: function (el) {
      return el && el.classList.contains('is-open');
    }
  };

  on(doc, 'keydown', function (event) {
    if (event.key === 'Escape' && openLayers.length) {
      Layer.close();
    }
  });

  // Generic [data-layer-close] handling
  on(doc, 'click', function (event) {
    var closer = event.target.closest('[data-layer-close]');
    if (!closer) return;
    event.preventDefault();
    var target = closer.getAttribute('data-layer-close');
    Layer.close(target ? doc.getElementById(target) : null);
  });

  /* --------------------------------------------------------------- header */

  // Re-running init in the theme editor must not stack listeners.
  var teardownHeader = null;

  function once(el, flag) {
    if (!el || el.getAttribute(flag) === 'true') return false;
    el.setAttribute(flag, 'true');
    return true;
  }

  function initHeader() {
    var header = $('[data-header]');
    if (!header) return;

    if (teardownHeader) teardownHeader();

    var shell = header.closest('.shell__header') || header;
    var announcement = $('[data-announcement]');
    var overlay = doc.body.classList.contains('header--overlay');
    var lastY = window.scrollY;
    var threshold = 8;
    var announcementH = 0;

    function measure() {
      announcementH = announcement ? announcement.offsetHeight : 0;
      root.style.setProperty('--header-h', header.offsetHeight + 'px');
      root.style.setProperty('--announcement-h', announcementH + 'px');
    }

    // Determine the colour scheme of whatever sits beneath the header.
    var schemeTargets = $$('[data-section-scheme]');

    function updateScheme() {
      var probe = header.offsetHeight * 0.5;
      var scheme = 'light';

      for (var i = 0; i < schemeTargets.length; i++) {
        var rect = schemeTargets[i].getBoundingClientRect();
        if (rect.top <= probe && rect.bottom > probe) {
          scheme = schemeTargets[i].getAttribute('data-section-scheme') || 'light';
        }
      }
      header.setAttribute('data-scheme', scheme === 'dark' ? 'dark' : 'light');
    }

    var update = rafThrottle(function () {
      // Freeze while a drawer/overlay is open. Locking the scroll pins body to
      // position:fixed, which drops window.scrollY to 0 and fires a scroll
      // event — the header would otherwise think it is back at the top, go
      // transparent, and slide down, leaving the drawer misaligned.
      if (openLayers.length) return;

      var y = window.scrollY;

      header.classList.toggle('is-solid', y > threshold + announcementH);

      // In overlay mode the fixed block slides up by the announcement height,
      // so the bar leaves the viewport and the header lands flush at the top.
      // Uses `top`, not `transform` — see the note in theme.css; a transform
      // here would trap the fixed-position menu drawer inside this element.
      if (overlay) {
        var offset = Math.min(y, announcementH);
        shell.style.top = offset ? -offset + 'px' : '';
      }

      if (header.hasAttribute('data-hide-on-scroll')) {
        var goingDown = y > lastY && y > header.offsetHeight * 3;
        shell.classList.toggle('is-hidden', goingDown && !openLayers.length);
      }

      lastY = y;
      updateScheme();
    });

    var onResize = debounce(function () {
      measure();
      update();
    }, 150);

    measure();
    update();
    on(window, 'scroll', update, { passive: true });
    on(window, 'resize', onResize);

    // Mobile menu
    var drawer = $('[data-menu-drawer]');
    var burgers = $$('[data-menu-toggle]');
    var onDrawerResize = debounce(function () {
      if (window.innerWidth >= 990 && !drawer.hasAttribute('data-desktop-menu') && Layer.isOpen(drawer)) Layer.close(drawer);
    }, 200);

    teardownHeader = function () {
      window.removeEventListener('scroll', update);
      window.removeEventListener('resize', onResize);
      window.removeEventListener('resize', onDrawerResize);
      teardownHeader = null;
    };

    function setBurgers(expanded) {
      burgers.forEach(function (b) {
        b.setAttribute('aria-expanded', expanded ? 'true' : 'false');
      });
    }

    // The drawer starts at the header's bottom edge, which moves as the
    // announcement bar scrolls away, so it is measured at open time.
    function positionDrawer() {
      if (!drawer) return;
      var bottom = header.getBoundingClientRect().bottom;
      drawer.style.setProperty('--drawer-top', Math.max(0, Math.round(bottom)) + 'px');
    }

    burgers.forEach(function (burger) {
      // Without this guard a second binding would fire on the same click and
      // immediately re-close the drawer, so it would never appear to open.
      if (!once(burger, 'data-bound')) return;
      on(burger, 'click', function () {
        if (Layer.isOpen(drawer)) {
          Layer.close(drawer);
        } else {
          positionDrawer();
          // Match the drawer's paper background so the X reads against it.
          header.classList.add('is-solid');
          header.setAttribute('data-scheme', 'light');
          setBurgers(true);
          Layer.open(drawer, {
            // A disclosure panel: focus stays on the toggle, Escape still closes.
            trap: false,
            focus: false,
            onClose: function () {
              setBurgers(false);
              closeSubmenus();
              update(); // restore solidity and scheme from the scroll position
            }
          });
          window.requestAnimationFrame(positionDrawer);
        }
      });
    });

    on(window, 'resize', debounce(function () {
      if (Layer.isOpen(drawer)) positionDrawer();
    }, 120));

    // On desktop the drawer is a side panel; a click on the dimmed area
    // beside it lands on the drawer element itself and closes it.
    if (once(drawer, 'data-bound')) {
      on(drawer, 'click', function (event) {
        if (event.target === drawer) Layer.close(drawer);
      });
    }

    // Nested menus (Brands) slide in as their own panel with a Back button.
    function closeSubmenus() {
      $$('[data-submenu].is-active', drawer).forEach(function (panel) {
        panel.classList.remove('is-active');
        panel.setAttribute('aria-hidden', 'true');
      });
      $$('[data-submenu-open]', drawer).forEach(function (btn) {
        btn.setAttribute('aria-expanded', 'false');
      });
    }

    $$('[data-submenu-open]', drawer).forEach(function (btn) {
      if (!once(btn, 'data-bound')) return;
      on(btn, 'click', function () {
        var panel = doc.getElementById(btn.getAttribute('data-submenu-open'));
        if (!panel) return;
        panel.classList.add('is-active');
        panel.removeAttribute('aria-hidden');
        btn.setAttribute('aria-expanded', 'true');
        var back = $('[data-submenu-close]', panel);
        if (back) back.focus({ preventScroll: true });
      });
    });

    $$('[data-submenu-close]', drawer).forEach(function (btn) {
      if (!once(btn, 'data-bound')) return;
      on(btn, 'click', function () {
        var panel = btn.closest('[data-submenu]');
        var opener = panel && $('[data-submenu-open="' + panel.id + '"]', drawer);
        closeSubmenus();
        if (opener) opener.focus({ preventScroll: true });
      });
    });

    // Keep the drawer closed when resizing up to desktop.
    on(window, 'resize', onDrawerResize);
  }

  /* --------------------------------------------------------------- search */

  function initSearch() {
    var overlay = $('[data-search-overlay]');
    if (!overlay) return;

    var input = $('[data-search-input]', overlay);
    var results = $('[data-search-results]', overlay);
    var form = $('[data-search-form]', overlay);
    var controller = null;

    $$('[data-search-toggle]').forEach(function (btn) {
      if (!once(btn, 'data-bound')) return;
      on(btn, 'click', function (event) {
        event.preventDefault();
        Layer.close(); // close menu drawer if open
        Layer.open(overlay, { initialFocus: '[data-search-input]' });
      });
    });

    if (!once(overlay, 'data-bound')) return;

    $$('[data-search-suggestion]', overlay).forEach(function (btn) {
      on(btn, 'click', function () {
        input.value = btn.getAttribute('data-search-suggestion');
        input.focus();
        run();
      });
    });

    function clearResults() {
      if (!results) return;
      results.innerHTML = '';
      results.hidden = true;
    }

    var run = debounce(function () {
      if (!settings.predictiveSearch || !results) return;
      var q = input.value.trim();

      if (q.length < 2) {
        clearResults();
        return;
      }

      if (controller) controller.abort();
      controller = new AbortController();

      var url =
        routes.predictiveSearch +
        '?q=' +
        encodeURIComponent(q) +
        '&resources[type]=product,collection,query' +
        '&resources[limit]=6' +
        '&resources[options][unavailable_products]=last' +
        '&section_id=predictive-search';

      fetch(url, { signal: controller.signal })
        .then(function (res) {
          if (!res.ok) throw new Error('search');
          return res.text();
        })
        .then(function (text) {
          var parsed = new DOMParser().parseFromString(text, 'text/html');
          var payload = parsed.querySelector('[data-predictive-root]');
          results.innerHTML = payload ? payload.innerHTML : '';
          results.hidden = false;
        })
        .catch(function (err) {
          if (err.name !== 'AbortError') clearResults();
        });
    }, 240);

    on(input, 'input', run);
    on(form, 'submit', function (event) {
      if (!input.value.trim()) event.preventDefault();
    });
  }

  /* ----------------------------------------------------------------- cart */

  function refreshSections(ids) {
    return fetch(routes.root + '?sections=' + ids.join(','), {
      headers: { 'X-Requested-With': 'XMLHttpRequest' }
    })
      .then(function (res) {
        return res.json();
      })
      .then(function (payload) {
        ids.forEach(function (id) {
          if (!payload[id]) return;
          var parsed = new DOMParser().parseFromString(payload[id], 'text/html');
          var next = parsed.querySelector('[data-section-id="' + id + '"]');
          var current = doc.querySelector('[data-section-id="' + id + '"]');
          if (next && current) current.innerHTML = next.innerHTML;
        });
      })
      .catch(function () {
        /* leave the DOM untouched on failure */
      });
  }

  // Re-render the bag and sync every cart counter from a single request.
  function refreshCart() {
    return refreshSections(['bag-overlay']).then(function () {
      var state = $('[data-cart-state]');
      if (!state) return;
      var count = parseInt(state.getAttribute('data-count'), 10) || 0;

      $$('[data-cart-count]').forEach(function (el) {
        // The desktop link always shows a count, even at zero; the mobile icon
        // stays clean until something is in the cart.
        if (el.getAttribute('data-format') === 'parens') {
          el.textContent = '(' + count + ')';
        } else {
          el.textContent = count > 0 ? String(count) : '';
        }
      });

      $$('[data-bag-heading-count]').forEach(function (el) {
        el.textContent = count > 0 ? '(' + count + ')' : '';
      });

      $$('[data-bag-toggle]').forEach(function (el) {
        var label = el.getAttribute('data-label') || el.getAttribute('aria-label') || '';
        if (!el.getAttribute('data-label')) el.setAttribute('data-label', label.replace(/\s*\(\d+\)\s*$/, ''));
        el.setAttribute('aria-label', el.getAttribute('data-label') + ' (' + count + ')');
      });
    });
  }

  function initCart() {
    var overlay = $('[data-bag-overlay]');

    $$('[data-bag-toggle]').forEach(function (btn) {
      on(btn, 'click', function (event) {
        if (!overlay || !settings.cartOverlay) return; // fall through to /cart link
        event.preventDefault();
        Layer.close();
        Layer.open(overlay);
      });
    });

    // Add to cart (AJAX)
    on(doc, 'submit', function (event) {
      var form = event.target.closest('[data-product-form]');
      if (!form || !settings.cartOverlay) return;

      event.preventDefault();
      var button = $('[data-add-to-cart]', form);
      var original = button ? button.querySelector('span').textContent : '';

      if (button) {
        button.setAttribute('aria-disabled', 'true');
        button.querySelector('span').textContent = '…';
      }

      fetch(routes.cartAdd, {
        method: 'POST',
        headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
        body: new FormData(form)
      })
        .then(function (res) {
          return res.json().then(function (data) {
            if (!res.ok) throw new Error(data.description || data.message || strings.cartError);
            return data;
          });
        })
        .then(function () {
          return refreshCart();
        })
        .then(function () {
          announce(strings.addedToBag || 'Added to bag');
          if (overlay) Layer.open(overlay);
        })
        .catch(function (err) {
          var errorEl = $('[data-form-error]', form);
          if (errorEl) {
            errorEl.textContent = err.message || strings.cartError;
            errorEl.hidden = false;
          }
        })
        .finally(function () {
          if (button) {
            button.removeAttribute('aria-disabled');
            button.querySelector('span').textContent = original;
          }
        });
    });

    // Quantity + line item changes
    function changeLine(key, quantity, scope) {
      if (scope) scope.setAttribute('aria-busy', 'true');

      return fetch(routes.cartChange, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: key, quantity: quantity })
      })
        .then(function (res) {
          return res.json();
        })
        .then(function () {
          if (doc.body.classList.contains('template--cart')) {
            window.location.reload();
            return null;
          }
          return refreshCart();
        })
        .catch(function () {
          announce(strings.cartError || 'Something went wrong');
        });
    }

    on(doc, 'click', function (event) {
      var step = event.target.closest('[data-qty-step]');
      if (step) {
        var wrap = step.closest('[data-qty]');
        var field = $('input', wrap);
        var delta = parseInt(step.getAttribute('data-qty-step'), 10);
        var next = Math.max(parseInt(field.min || 0, 10), (parseInt(field.value, 10) || 0) + delta);
        field.value = next;
        field.dispatchEvent(new Event('change', { bubbles: true }));
        return;
      }

      var remove = event.target.closest('[data-line-remove]');
      if (remove) {
        event.preventDefault();
        changeLine(remove.getAttribute('data-line-remove'), 0, remove.closest('[data-line]'));
      }
    });

    on(doc, 'change', function (event) {
      var field = event.target.closest('[data-line-qty]');
      if (!field) return;
      changeLine(field.getAttribute('data-line-qty'), field.value, field.closest('[data-line]'));
    });
  }

  /* -------------------------------------------------------------- product */

  function initProduct(scope) {
    $$('[data-product-root]', scope || doc).forEach(function (rootEl) {
      var dataEl = $('[data-variants]', rootEl);
      if (!dataEl) return;

      var variants;
      try {
        variants = JSON.parse(dataEl.textContent);
      } catch (e) {
        return;
      }

      var form = $('[data-product-form]', rootEl);
      var idField = form ? $('[data-variant-id]', form) : null;
      var button = $('[data-add-to-cart]', rootEl);
      var priceEl = $('[data-price-target]', rootEl);
      var skuEl = $('[data-sku]', rootEl);
      var inputs = $$('[data-option-input]', rootEl);

      function selected() {
        var values = [];
        inputs.forEach(function (input) {
          if (input.type === 'radio') {
            if (input.checked) values[parseInt(input.getAttribute('data-option-index'), 10)] = input.value;
          } else {
            values[parseInt(input.getAttribute('data-option-index'), 10)] = input.value;
          }
        });
        return values;
      }

      function match(values) {
        return variants.find(function (variant) {
          return variant.options.every(function (option, index) {
            return option === values[index];
          });
        });
      }

      function update() {
        var values = selected();
        var variant = match(values);

        // Reflect selected value labels
        $$('[data-option-selected]', rootEl).forEach(function (el) {
          var index = parseInt(el.getAttribute('data-option-selected'), 10);
          if (values[index]) el.textContent = values[index];
        });

        if (!variant) {
          if (button) {
            button.setAttribute('aria-disabled', 'true');
            button.querySelector('span').textContent = strings.unavailable || 'Unavailable';
          }
          return;
        }

        if (idField) idField.value = variant.id;
        if (skuEl) skuEl.textContent = variant.sku || '';

        if (priceEl) {
          var html = formatMoney(variant.price);
          if (variant.compare_at_price && variant.compare_at_price > variant.price) {
            html = '<s>' + formatMoney(variant.compare_at_price) + '</s>' + html;
          }
          priceEl.innerHTML = html;
        }

        if (button) {
          if (variant.available) {
            button.removeAttribute('aria-disabled');
            button.querySelector('span').textContent = strings.addToBag || 'Add to bag';
          } else {
            button.setAttribute('aria-disabled', 'true');
            button.querySelector('span').textContent = strings.soldOut || 'Sold out';
          }
        }

        if (window.history.replaceState && rootEl.hasAttribute('data-update-url')) {
          var url = new URL(window.location.href);
          url.searchParams.set('variant', variant.id);
          window.history.replaceState({}, '', url.toString());
        }
      }

      inputs.forEach(function (input) {
        on(input, 'change', update);
      });

      update();

      // Gallery dots (mobile swipe indicator)
      var gallery = $('[data-gallery]', rootEl);
      var dots = $$('[data-gallery-dot]', rootEl);

      if (gallery && dots.length) {
        on(
          gallery,
          'scroll',
          rafThrottle(function () {
            var index = Math.round(gallery.scrollLeft / gallery.clientWidth);
            dots.forEach(function (dot, i) {
              dot.classList.toggle('is-active', i === index);
            });
          }),
          { passive: true }
        );
      }
    });
  }

  /* ------------------------------------------------------------ accordion */

  function initAccordions(scope) {
    $$('[data-accordion-item]', scope || doc).forEach(function (item) {
      var panel = $('[data-accordion-panel]', item);
      if (!panel || reduced) return;

      var summary = $('summary', item);

      on(summary, 'click', function (event) {
        event.preventDefault();

        if (item.open) {
          panel.style.height = panel.scrollHeight + 'px';
          window.requestAnimationFrame(function () {
            panel.style.transition = 'height 420ms cubic-bezier(0.22, 1, 0.36, 1)';
            panel.style.height = '0px';
          });
          window.setTimeout(function () {
            item.open = false;
            panel.style.transition = '';
            panel.style.height = '';
          }, 420);
        } else {
          item.open = true;
          panel.style.height = '0px';
          window.requestAnimationFrame(function () {
            panel.style.transition = 'height 420ms cubic-bezier(0.22, 1, 0.36, 1)';
            panel.style.height = panel.scrollHeight + 'px';
          });
          window.setTimeout(function () {
            panel.style.transition = '';
            panel.style.height = '';
          }, 420);
        }
      });
    });
  }

  /* --------------------------------------------------------- footer groups */

  // Collapsible below 750px; above it CSS forces them open and disables the
  // toggle, so the state attribute is simply ignored there.
  function initFooterGroups(scope) {
    $$('[data-footer-group]', scope || doc).forEach(function (group) {
      if (!once(group, 'data-bound')) return;
      var toggle = $('.footer__group-toggle', group);
      if (!toggle) return;

      on(toggle, 'click', function () {
        var open = group.hasAttribute('data-open');
        if (open) {
          group.removeAttribute('data-open');
        } else {
          group.setAttribute('data-open', '');
        }
        toggle.setAttribute('aria-expanded', open ? 'false' : 'true');
      });
    });
  }

  /* ------------------------------------------------------------- scrollers */

  // Touch devices swipe these natively. With a mouse or trackpad-as-pointer
  // there is no way to drag a horizontal scroller, so it looks broken — this
  // adds click-and-drag and a grab cursor when the row actually overflows.
  function initScrollers(scope) {
    $$('.scroller', scope || doc).forEach(function (el) {
      if (!once(el, 'data-drag-bound')) return;

      function sync() {
        el.classList.toggle('is-draggable', el.scrollWidth > el.clientWidth + 2);
      }

      sync();
      on(window, 'resize', debounce(sync, 150));

      var dragging = false;
      var startX = 0;
      var startLeft = 0;
      var moved = 0;

      on(el, 'pointerdown', function (event) {
        if (event.pointerType === 'touch') return; // native scrolling is better
        if (el.scrollWidth <= el.clientWidth) return;
        dragging = true;
        moved = 0;
        startX = event.clientX;
        startLeft = el.scrollLeft;
        el.classList.add('is-dragging');
        try {
          el.setPointerCapture(event.pointerId);
        } catch (e) {
          /* not captureable — dragging still works */
        }
      });

      on(el, 'pointermove', function (event) {
        if (!dragging) return;
        var dx = event.clientX - startX;
        moved = Math.max(moved, Math.abs(dx));
        el.scrollLeft = startLeft - dx;
      });

      function release(event) {
        if (!dragging) return;
        dragging = false;
        el.classList.remove('is-dragging');
        try {
          el.releasePointerCapture(event.pointerId);
        } catch (e) {
          /* nothing to release */
        }
      }

      on(el, 'pointerup', release);
      on(el, 'pointercancel', release);

      // A drag must not also follow the product link underneath it.
      on(
        el,
        'click',
        function (event) {
          if (moved > 6) {
            event.preventDefault();
            event.stopPropagation();
          }
        },
        true
      );
    });
  }

  /* -------------------------------------------------------------- reveals */

  function initReveals(scope) {
    var items = $$('.reveal, .reveal-media', scope || doc).filter(function (el) {
      return !el.classList.contains('is-in');
    });

    if (!settings.reveals || reduced || !('IntersectionObserver' in window)) {
      items.forEach(function (el) {
        el.classList.add('is-in');
      });
      return;
    }

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-in');
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.06 }
    );

    items.forEach(function (el) {
      observer.observe(el);
    });
  }

  /* --------------------------------------------------------------- panels */

  function initPanels() {
    $$('[data-panel-toggle]').forEach(function (btn) {
      if (!once(btn, 'data-bound')) return;
      on(btn, 'click', function (event) {
        event.preventDefault();
        var panel = doc.getElementById(btn.getAttribute('data-panel-toggle'));
        if (!panel) return;
        if (Layer.isOpen(panel)) {
          Layer.close(panel);
        } else {
          Layer.open(panel);
        }
      });
    });
  }

  /* ------------------------------------------------------------- collection */

  function initCollection() {
    var sort = $('[data-sort]');
    if (sort) {
      on(sort, 'change', function () {
        var url = new URL(window.location.href);
        url.searchParams.set('sort_by', sort.value);
        url.searchParams.delete('page');
        window.location.href = url.toString();
      });
    }

    var filterForm = $('[data-filter-form]');
    if (filterForm) {
      on(filterForm, 'change', function () {
        if (filterForm.hasAttribute('data-auto-submit')) filterForm.submit();
      });
    }
  }

  /* ----------------------------------------------------- page transitions */

  function initPageTransitions() {
    root.classList.add('theme-ready');
    if (!settings.pageTransitions || reduced) return;

    on(doc, 'click', function (event) {
      var link = event.target.closest('a');
      if (!link) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
      if (link.target === '_blank' || link.hasAttribute('download')) return;
      if (link.hasAttribute('data-no-transition')) return;

      var href = link.getAttribute('href') || '';
      if (!href || href.charAt(0) === '#' || href.indexOf('mailto:') === 0 || href.indexOf('tel:') === 0) return;

      var url;
      try {
        url = new URL(link.href);
      } catch (e) {
        return;
      }
      if (url.origin !== window.location.origin) return;
      if (url.pathname === window.location.pathname && url.search === window.location.search) return;

      event.preventDefault();
      root.classList.add('is-leaving');
      window.setTimeout(function () {
        window.location.href = link.href;
      }, 280);
    });

    on(window, 'pageshow', function (event) {
      if (event.persisted) root.classList.remove('is-leaving');
    });
  }

  /* ----------------------------------------------------------------- boot */

  function boot(scope) {
    initReveals(scope);
    initProduct(scope);
    initAccordions(scope);
    initScrollers(scope);
    initFooterGroups(scope);
  }

  function init() {
    initHeader();
    initSearch();
    initCart();
    initPanels();
    initCollection();
    boot(doc);
    initPageTransitions();
  }

  if (doc.readyState === 'loading') {
    on(doc, 'DOMContentLoaded', init);
  } else {
    init();
  }

  /* Shopify theme editor hooks */
  doc.addEventListener('shopify:section:load', function (event) {
    boot(event.target);
    initPanels();
    // Only re-initialise a subsystem when its own section actually reloaded.
    if (event.target.querySelector('[data-header]')) initHeader();
    if (event.target.querySelector('[data-search-overlay]')) initSearch();
  });

  // Only sections that opt in are previewed open when selected in the editor.
  // The header must NOT — it contains the mobile menu, which would otherwise
  // take over the screen every time a merchant clicks Header.
  doc.addEventListener('shopify:section:select', function (event) {
    var preview = event.target.querySelector('[data-editor-preview]');
    if (preview) Layer.open(preview);
  });

  doc.addEventListener('shopify:section:deselect', function (event) {
    var preview = event.target.querySelector('[data-editor-preview]');
    if (preview) Layer.close(preview);
  });

  window.theme.Layer = Layer;
  window.theme.refreshSections = refreshSections;
  window.theme.refreshCart = refreshCart;
  window.theme.boot = boot;
})();
